import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import './index.css'
import './themes.css'
import './design-system/index.css'
import App from './App.jsx'
import { initializeFontSettings } from './store/readerFont$'
import { initSentry } from './lib/sentry'
import { initNativeFeatures, hideSplashScreen } from './lib/native'
import { supabase } from './lib/supabase'

// Initialize Sentry BEFORE React renders (catches initialization errors)
initSentry();

// Plan A: Catch failed dynamic import errors and auto-reload.
// After a new deploy, Vercel removes old hashed chunk files. Any running page still
// referencing old chunk URLs will get a "Failed to fetch dynamically imported module"
// error. Reload immediately to pick up the new HTML + new chunk URLs.
// Guard: sessionStorage timestamp prevents an infinite reload loop if the new build
// itself is broken (only allows one reload attempt per 30 seconds).
window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason?.message || '';
  if (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  ) {
    const lastReload = parseInt(sessionStorage.getItem('_chunkErrReload') || '0', 10);
    if (Date.now() - lastReload > 30_000) {
      sessionStorage.setItem('_chunkErrReload', String(Date.now()));
      window.location.reload();
    }
  }
});

// Initialize Capacitor native features (status bar, keyboard, app lifecycle)
initNativeFeatures();

// Initialize font settings from localStorage on app load
initializeFontSettings();

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

// Hide splash screen after React renders (give UI time to load)
setTimeout(() => {
  hideSplashScreen();
}, 500);

// Plan B: Reload the page when a new service worker takes control.
// After skipWaiting + clientsClaim activate the new SW, the running page still holds
// references to old hashed chunk URLs that no longer exist on the CDN. Reloading gives
// the page fresh HTML with the new chunk URLs, eliminating the mismatch.
// First-install guard: if no SW was controlling the page when this runs, the first
// controllerchange event is the initial install (not an update) — skip that reload.
if ('serviceWorker' in navigator) {
  let swWasControlling = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (swWasControlling) {
      window.location.reload();
    }
    swWasControlling = true;
  });
}

// Register PWA Service Worker with auto-update
// This will automatically update when a new version is available
registerSW({
  onOfflineReady() {
    console.log('App ready to work offline');
  },
  onRegistered(registration) {
    console.log('SW registered:', registration?.scope);

    if (registration) {
      // Check for updates every 5 minutes when online
      setInterval(() => {
        registration.update();
      }, 5 * 60 * 1000);

      // Also check when user returns to the tab (visibility change)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          registration.update();
        }
      });
    }
  },
  onRegisterError(error) {
    console.error('SW registration failed:', error);
  }
});

// Force-close Supabase realtime channels on page unload
// Prevents 3-5s delay waiting for server acknowledgment
window.addEventListener('pagehide', () => {
  supabase.removeAllChannels();
});
